import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Clock, Users, MessageSquare, CheckCircle } from 'lucide-react';

const MandarinClassesLandingPage = () => {
  useEffect(() => {
    document.title = 'Mandarin Group Classes in Hong Kong';
  }, []);

  return (
    <div className="bg-[#131313] text-[#e2e2e2] font-body min-h-screen">
      <Navigation />

      <style>{`
        .text-gradient-mandarin {
          background: linear-gradient(135deg, #cdbdff 0%, #7e55f6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      <main className="pt-16">

        {/* ── Hero Section ── */}
        <section className="relative min-h-[819px] flex items-center px-8 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-r from-[#131313] via-[rgba(19,19,19,0.8)] to-transparent z-10" />
            <img
              className="w-full h-full object-cover opacity-40"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuB087T9Maz2g17G39an4lOUM8ADAst86i17qkLUpXo6OUc1VBgwK_SpyY_vOoYCLaGbU22NOzOhMB_UJXXnGbIDgRxs5JDGY_uObvR9KD7A5TMGrkW4FDd8-WkIAlZM0Z0DDB9kTmBpdUa-RRIDpyjc2TtZa5u7VJnPDxqUwg513iZ9RU0zIPcvhCrzxGhKLiSvrMms8c6mSe1ByZixZj8PgZMg8ohM9mu8VCSaZwLTThSNaq68Pd2rGXxUZZqHTZCYxrzqTmsgtPTc"
              alt="Hong Kong Victoria Harbour at night"
            />
          </div>

          <div className="max-w-7xl mx-auto w-full relative z-20 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block px-4 py-1 rounded-full bg-[#353535] text-[#cdbdff] font-semibold text-sm tracking-widest uppercase">
                Mandarin Group Classes in Hong Kong
              </div>
              <h1 className="text-6xl md:text-8xl font-extrabold font-headline leading-none tracking-tighter text-[#e2e2e2]">
                Master Mandarin <br />
                <span className="text-gradient-mandarin">with Confidence</span>
              </h1>
              <p className="text-xl text-[#cac3d7] max-w-lg leading-relaxed">
                Navigate the professional landscape of Hong Kong with elite Mandarin fluency. Group lessons are available in Central, online, or at your home or office.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a
                  href="#group-class-packages"
                  className="bg-purple-600 hover:bg-purple-700 text-white px-10 py-5 rounded-full font-bold text-lg text-center active:scale-95 transition-colors"
                >
                  Join a Group Class
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── Features Bento Grid ── */}
        <section id="curriculum" className="py-24 px-8 max-w-7xl mx-auto">
          <div className="mb-16 text-center md:text-left">
            <h2 className="text-4xl md:text-5xl font-bold font-headline mb-4">
              Total Flexibility.{' '}
              <span className="text-[#7e55f6]">Zero Compromise.</span>
            </h2>
            <p className="text-[#cac3d7] text-lg">We break the rigid structure of traditional language schools.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Large Card */}
            <div className="md:col-span-2 bg-[#1f1f1f] rounded-3xl p-10 flex flex-col justify-between overflow-hidden relative">
              <div className="relative z-10">
                <Clock size={48} className="text-[#cdbdff] mb-6" />
                <h3 className="text-3xl font-bold mb-4">
                  The Power of Choice: <br />1h or 2h Classes
                </h3>
                <p className="text-[#cac3d7] max-w-md text-lg leading-relaxed">
                  Unlike other schools that lock you into fixed durations, we offer the unique flexibility to choose between focused 1-hour sessions or immersive 2-hour intensives depending on your weekly schedule.
                </p>
              </div>
              <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[rgba(205,189,255,0.1)] to-transparent pointer-events-none" />
            </div>

            {/* Small Card 1 */}
            <div className="bg-[#1b1b1b] rounded-3xl p-10 flex flex-col">
              <Users size={40} className="text-[#cdbdff] mb-6" />
              <h3 className="text-2xl font-bold mb-4">Intimate Groups</h3>
              <p className="text-[#cac3d7]">
                Max 6 students. Guaranteed speaking time for everyone. You aren't just a face in the crowd; you're part of the conversation.
              </p>
            </div>

            {/* Small Card 2 */}
            <div className="bg-[#1b1b1b] rounded-3xl p-10 flex flex-col">
              <MessageSquare size={40} className="text-[#cdbdff] mb-6" />
              <h3 className="text-2xl font-bold mb-4">Real Conversation</h3>
              <p className="text-[#cac3d7]">
                Practical practice centered on Hong Kong life and business contexts. Master the Mandarin used in real boardroom meetings.
              </p>
            </div>

            {/* Long Card */}
            <div className="md:col-span-2 bg-[#353535] rounded-3xl p-10 flex items-center gap-8">
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-4">Flexible Schedule</h3>
                <p className="text-[#cac3d7]">
                  Classes scheduled for the busy professional. Mid-day breaks or late evening sessions — we align with the rhythm of the city.
                </p>
              </div>
              <div className="hidden md:block w-48 h-32 rounded-2xl overflow-hidden shadow-xl flex-shrink-0">
                <img
                  className="w-full h-full object-cover"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuAmaA76VZwqNkJ6ygkG87PRxmG1OJXhN74vRTdr0M3rbIDc6OcUFos30-j5jtnUrAiG7q4DN1oe96nYuZ5ZzfLir6LSGgiucJdb0pcKegIzuhEdvR06LpmGXn6WpKi0BRDwnM_rXC92N5ANsYerrEVxKDM03kfBWoKYXdE_VejRFlMPIHZK8BEteE89c5ht0WeXMy5k8lriTW8bQbauyeLvS2QEra40urCMR6sFMxhkwk4ZfsoZtKUCr-CObwZ2iQUlJXhPHGR13ByB"
                  alt="Modern Hong Kong office at evening"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── Pricing Section ── */}
        <section id="group-class-packages" className="py-24 px-8 bg-[#1b1b1b]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-headline mb-4">Group Class Packages</h2>
              <p className="text-[#cac3d7] text-lg">We will match you with students at a similar level and goal so the learning stays focused and motivating.</p>
            </div>

            <div className="grid grid-cols-1 gap-8 max-w-md mx-auto">
              {/* Starter */}
              <div className="bg-[#1f1f1f] rounded-3xl p-10 flex flex-col border border-[rgba(73,68,85,0.1)] shadow-lg">
                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-2">Starter</h3>
                  <div className="text-5xl font-extrabold font-headline text-[#e2e2e2]">HKD 3,000</div>
                  <div className="text-[#cac3d7] mt-2">10 one-hour classes</div>
                </div>
                <ul className="space-y-4 mb-10 flex-1">
                  {['Max 6 students per class', 'Conversation focused practice', 'Digital learning materials'].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-[#cdbdff] flex-shrink-0" />
                      <span className="text-[#cac3d7]">{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://book.mandarintutorhk.com/start/mandarin"
                  className="w-full py-4 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-bold transition-colors text-center block"
                >
                  Select Starter
                </a>
              </div>

            </div>
          </div>
        </section>

        {/* ── Tutor Grid ── */}
        <section className="py-24 px-8 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-headline mb-4">Our Instructors</h2>
            <p className="text-[#cac3d7] text-lg">Masters of linguistics and cross-cultural communication.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                name: 'Sophia Chu',
                title: 'Business Linguistics Specialist',
                bio: 'Helping executives master the art of negotiation and cultural nuance in the boardroom.',
                img: 'https://fra.cloud.appwrite.io/v1/storage/buckets/tutor-files/files/695b336d002447ac7295/view?project=mandarin-cantonese-classes',
              },
              {
                name: 'Aileen Ting',
                title: 'Fluency & Tone Architect',
                bio: 'Specializing in rapid-immersion techniques and perfect pitch for beginner to intermediate levels.',
                img: 'https://static.wixstatic.com/media/925357_c6ca0fc7eda940c2809745540c8c8870~mv2.jpg/v1/fill/w_768,h_770,al_c,q_85,enc_avif,quality_auto/925357_c6ca0fc7eda940c2809745540c8c8870~mv2.jpg',
              },
              {
                name: 'Sandy Wang',
                title: 'Conversational Flow Master',
                bio: 'Focusing on modern slang, colloquialisms, and day-to-day interactions in a high-speed city.',
                img: 'https://fra.cloud.appwrite.io/v1/storage/buckets/tutor-files/files/696c667c0002758db2b0/view?project=mandarin-cantonese-classes',
              },
              {
                name: 'Yi Fei Zhou',
                title: 'HSK & Certification Coach',
                bio: 'Academic rigor meets practical application for those seeking official proficiency benchmarks.',
                img: 'https://fra.cloud.appwrite.io/v1/storage/buckets/tutor-files/files/698806c9002a1f32aa87/view?project=mandarin-cantonese-classes',
              },
            ].map((tutor) => (
              <div key={tutor.name} className="group">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden mb-6 bg-[#1f1f1f] relative">
                  <img
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    src={tutor.img}
                    alt={tutor.name}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#131313] to-transparent opacity-60" />
                </div>
                <h4 className="text-xl font-bold font-headline">{tutor.name}</h4>
                <p className="text-[#cdbdff] font-semibold text-sm mb-2">{tutor.title}</p>
                <p className="text-[#cac3d7] text-sm">{tutor.bio}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Google Reviews ── */}
        <section className="py-24 px-8 bg-[#131313]">
          <div className="max-w-7xl mx-auto">
            {/* Header bar */}
            <div className="bg-[#1f1f1f] rounded-2xl px-8 py-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-[rgba(73,68,85,0.2)]">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <svg viewBox="0 0 24 24" width="26" height="26" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="text-xl font-bold text-[#e2e2e2]">Reviews</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-3xl font-extrabold text-[#e2e2e2]">5.0</span>
                  <span className="text-yellow-400 text-xl tracking-tight">★★★★★</span>
                  <span className="text-[#cac3d7]">(52)</span>
                </div>
              </div>
              <a
                href="https://maps.app.goo.gl/Vpp9EKR8gBGQu5z76"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-6 py-3 rounded-full transition-colors no-underline whitespace-nowrap self-start sm:self-auto"
              >
                Review us on Google
              </a>
            </div>

            {/* Cards row */}
            <div className="flex gap-5 overflow-x-auto pb-4" style={{ scrollbarWidth: 'none' }}>
              {/* AI Summary Card */}
              <div className="bg-[#1f1f1f] rounded-2xl p-6 flex flex-col border border-[rgba(205,189,255,0.2)] flex-shrink-0 w-60">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#cdbdff] to-[#7e55f6] flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2L9.09 8.26L2 9.27L7 14.14L5.82 21.02L12 17.77L18.18 21.02L17 14.14L22 9.27L14.91 8.26L12 2Z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-bold text-[#e2e2e2] text-sm leading-tight">AI-Generated Summary</div>
                    <div className="text-[#cac3d7] text-xs mt-0.5">Based on 52 Google reviews</div>
                  </div>
                </div>
                <div className="text-yellow-400 text-base mb-4">★★★★★</div>
                <ul className="space-y-3 text-sm text-[#cac3d7]">
                  {[
                    "Aileen's teaching style is patient and adaptable, catering to each student's unique learning pace.",
                    "Students report significant progress in their language skills, particularly in speaking and comprehension.",
                    "Her classes are well-structured, engaging, and tailored to meet the specific interests and needs of her students.",
                  ].map((point) => (
                    <li key={point} className="flex gap-2">
                      <span className="text-[#cdbdff] flex-shrink-0 font-bold">✓</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Individual review cards */}
              {[
                {
                  name: 'Vanessa Taylor',
                  initials: 'V',
                  color: '#c0392b',
                  time: '2 days ago',
                  text: 'I found that my time with Aileen was well spent … I left with more clarity in terms of direction and approach. She came across as a committed and caring tutor, who is fun, flexible yet focused when necessary.',
                },
                {
                  name: 'Emma J',
                  initials: 'E',
                  color: '#e67e22',
                  time: '5 days ago',
                  text: 'We took Mandarin and Cantonese classes with Aileen as a family. She adapted the lessons to our pace, was very patient and also made sure what we were learning was relevant to what we wanted to do. Highly recommended!',
                },
                {
                  name: 'Jed Walsh',
                  initials: 'J',
                  color: '#5b6ea8',
                  time: '12 days ago',
                  text: 'I would recommend the classes, the reference to textbooks and pdfs were useful.',
                  readMore: true,
                },
                {
                  name: 'Sam Mathews',
                  initials: 'S',
                  color: '#16a085',
                  time: '16 days ago',
                  text: 'Aileen has made learning Chinese both fun and interesting. She is both patient and flexible in helping students achieve their aims. Aileen can adapt lessons to support learning.',
                  readMore: true,
                },
              ].map((review) => (
                <div key={review.name} className="bg-[#1f1f1f] rounded-2xl p-6 flex flex-col border border-[rgba(73,68,85,0.2)] flex-shrink-0 w-60">
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                      style={{ backgroundColor: review.color }}
                    >
                      {review.initials}
                    </div>
                    <div>
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-[#e2e2e2] text-sm">{review.name}</span>
                        <svg className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                        </svg>
                      </div>
                      <div className="text-[#cac3d7] text-xs mt-0.5">{review.time}</div>
                    </div>
                  </div>
                  <div className="text-yellow-400 text-base mb-4">★★★★★</div>
                  <p className="text-sm text-[#cac3d7] flex-1 leading-relaxed">{review.text}</p>
                  {review.readMore && (
                    <a
                      href="https://maps.app.goo.gl/Vpp9EKR8gBGQu5z76"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#cdbdff] text-sm mt-3 no-underline hover:underline"
                    >
                      Read more
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Final CTA ── */}
        <section className="py-24 px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-[rgba(205,189,255,0.05)] -z-10" />
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl md:text-6xl font-extrabold font-headline mb-8 tracking-tighter">
              Ready to master <br />
              <span className="text-gradient-mandarin">Mandarin in HK?</span>
            </h2>
            <p className="text-xl text-[#cac3d7] mb-12 max-w-2xl mx-auto leading-relaxed">
              Everyone at the table speaks Mandarin. Isn't it time you did too?
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-6">
              <a
                href="#group-class-packages"
                className="bg-purple-600 hover:bg-purple-700 text-white font-black py-5 px-12 rounded-full text-xl active:scale-95 transition-colors font-headline text-center"
              >
                Join a Group Class
              </a>
              <a
                href="mailto:aileen@mandarintutorhk.com"
                className="bg-[#2a2a2a] text-[#e2e2e2] font-bold py-5 px-12 rounded-full text-xl hover:bg-[#353535] transition-colors font-headline text-center"
              >
                Contact Admissions
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MandarinClassesLandingPage;